import { BarChart3, Calculator, FileText, Info, Languages, SlidersHorizontal, Tag } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';

import { Button } from '@/components/Common/button';
import yaml from 'js-yaml';

interface PromptConfig {
  system: string;
  user: string;
}

interface PromptsData {
  summarization?: PromptConfig;
  classification?: PromptConfig;
  translation?: PromptConfig;
  entity_normalization?: PromptConfig;
  dimensional_analysis?: PromptConfig;
  quality_metrics?: PromptConfig;
}

interface PromptsFormProps {
  initialContent?: string;
  onSave: (yamlContent: string) => Promise<void>;
  onCancel: () => void;
}

const PROMPT_CATEGORIES = [
  { id: 'summarization', labelKey: 'admin.configForms.prompts.categories.summarization', icon: FileText },
  { id: 'classification', labelKey: 'admin.configForms.prompts.categories.classification', icon: Tag },
  { id: 'translation', labelKey: 'admin.configForms.prompts.categories.translation', icon: Languages },
  { id: 'entity_normalization', labelKey: 'admin.configForms.prompts.categories.entityNormalization', icon: SlidersHorizontal },
  { id: 'dimensional_analysis', labelKey: 'admin.configForms.prompts.categories.dimensionalAnalysis', icon: Calculator },
  { id: 'quality_metrics', labelKey: 'admin.configForms.prompts.categories.qualityMetrics', icon: BarChart3 },
];

export function PromptsForm({ initialContent, onSave, onCancel }: PromptsFormProps) {
  const { t } = useTranslation();
  const [prompts, setPrompts] = useState<PromptsData>({
    summarization: { system: '', user: '' },
    classification: { system: '', user: '' },
    translation: { system: '', user: '' },
    entity_normalization: { system: '', user: '' },
    dimensional_analysis: { system: '', user: '' },
    quality_metrics: { system: '', user: '' },
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [saving, setSaving] = useState(false);

  // Parse initial YAML content
  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = yaml.load(initialContent) as PromptsData;

        // Merge with default structure
        const newPrompts: PromptsData = {
          summarization: {
            system: parsed.summarization?.system || '',
            user: parsed.summarization?.user || '',
          },
          classification: {
            system: parsed.classification?.system || '',
            user: parsed.classification?.user || '',
          },
          translation: {
            system: parsed.translation?.system || '',
            user: parsed.translation?.user || '',
          },
          entity_normalization: {
            system: parsed.entity_normalization?.system || '',
            user: parsed.entity_normalization?.user || '',
          },
          dimensional_analysis: {
            system: parsed.dimensional_analysis?.system || '',
            user: parsed.dimensional_analysis?.user || '',
          },
          quality_metrics: {
            system: parsed.quality_metrics?.system || '',
            user: parsed.quality_metrics?.user || '',
          },
        };

        setPrompts(newPrompts);
      } catch (error) {
        console.error('Failed to parse YAML:', error);
      }
    }
  }, [initialContent]);

  const handleUpdatePrompt = (
    category: keyof PromptsData,
    field: 'system' | 'user',
    value: string
  ) => {
    setPrompts(prev => ({
      ...prev,
      [category]: {
        ...prev[category]!,
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const yamlContent = yaml.dump(prompts, {
        indent: 2,
        lineWidth: -1,
        noRefs: true,
        styles: {
          '!!str': 'literal', // Use | style for multiline strings
        },
      });

      await onSave(yamlContent);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto px-1">
      {/* Tabs for Prompt Categories */}
      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <Tab.List className="flex flex-wrap gap-2 rounded-xl bg-muted p-1 mb-4">
          {PROMPT_CATEGORIES.map((category) => {
            const Icon = category.icon;
            return (
              <Tab
                key={category.id}
                className={({ selected }) =>
                  `flex-1 min-w-[140px] rounded-lg px-3 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2
                  ${selected ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'}`
                }
              >
                <Icon className="h-4 w-4" />
                {t(category.labelKey)}
              </Tab>
            );
          })}
        </Tab.List>

        <Tab.Panels>
          {PROMPT_CATEGORIES.map((category) => {
            const promptConfig = prompts[category.id as keyof PromptsData];

            return (
              <Tab.Panel key={category.id}>
                <div className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-3 text-xs text-blue-800 dark:text-blue-200">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold mb-1">
                          {t('admin.configForms.prompts.promptConfigTitle', { label: t(category.labelKey) })}
                        </p>
                        <p>{t('admin.configForms.prompts.promptConfigDescription')}</p>
                      </div>
                    </div>
                  </div>

                  {/* System Prompt */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('admin.configForms.shared.systemPrompt')}
                      <span className="text-xs text-muted-foreground ml-2">
                        {t('admin.configForms.prompts.systemPromptHint')}
                      </span>
                    </label>
                    <textarea
                      value={promptConfig?.system || ''}
                      onChange={(e) => handleUpdatePrompt(category.id as keyof PromptsData, 'system', e.target.value)}
                      placeholder={t('admin.configForms.prompts.systemPromptPlaceholder')}
                      className="w-full px-3 py-2 text-sm font-mono bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      rows={8}
                    />
                  </div>

                  {/* User Prompt */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('admin.configForms.shared.userPrompt')}
                      <span className="text-xs text-muted-foreground ml-2">
                        {t('admin.configForms.prompts.userPromptHint')}
                      </span>
                    </label>
                    <textarea
                      value={promptConfig?.user || ''}
                      onChange={(e) => handleUpdatePrompt(category.id as keyof PromptsData, 'user', e.target.value)}
                      placeholder={t('admin.configForms.prompts.userPromptPlaceholder')}
                      className="w-full px-3 py-2 text-sm font-mono bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      rows={8}
                    />
                  </div>
                </div>
              </Tab.Panel>
            );
          })}
        </Tab.Panels>
      </Tab.Group>

      {/* Actions */}
      <div className="flex gap-3 justify-end pt-4 border-t border-border sticky bottom-0 bg-background">
        <Button onClick={onCancel} variant="outline" disabled={saving}>
          {t('actions.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? t('actions.saving') : t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
